if (!customElements.get('product-card-carousel')) {
  class ProductCardCarousel extends HTMLElement {
    constructor() {
      super();

      this.rafId = null;
      this.startX = 0;
      this.startY = 0;
      this.wasDragged = false;
      this.currentIndex = 0;

      this.handleScroll = this.handleScroll.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handleTrackClick = this.handleTrackClick.bind(this);
      this.showPrevious = this.showPrevious.bind(this);
      this.showNext = this.showNext.bind(this);
      this.handleBreakpointChange =
        this.handleBreakpointChange.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.track = this.querySelector('[data-carousel-track]');

      this.slides = Array.from(
        this.querySelectorAll(
          '.card-product-carousel__slide'
        )
      );

      this.dots = Array.from(
        this.querySelectorAll('[data-carousel-dot]')
      );

      this.previousButton = this.querySelector(
        '[data-carousel-previous]'
      );

      this.nextButton = this.querySelector(
        '[data-carousel-next]'
      );

      this.desktopBreakpoint = window.matchMedia(
        '(min-width: 990px)'
      );

      if (!this.track || this.slides.length < 2) return;

      this.track.addEventListener(
        'scroll',
        this.handleScroll,
        { passive: true }
      );

      this.track.addEventListener(
        'pointerdown',
        this.handlePointerDown,
        { passive: true }
      );

      this.track.addEventListener(
        'pointermove',
        this.handlePointerMove,
        { passive: true }
      );

      this.track.addEventListener(
        'click',
        this.handleTrackClick,
        true
      );

      if (this.previousButton) {
        this.previousButton.addEventListener(
          'click',
          this.showPrevious
        );
      }

      if (this.nextButton) {
        this.nextButton.addEventListener(
          'click',
          this.showNext
        );
      }

      if (this.desktopBreakpoint.addEventListener) {
        this.desktopBreakpoint.addEventListener(
          'change',
          this.handleBreakpointChange
        );
      } else {
        this.desktopBreakpoint.addListener(
          this.handleBreakpointChange
        );
      }

      this.dataset.initialized = 'true';
      this.applyCurrentMode();
    }

    disconnectedCallback() {
      if (!this.track) return;

      this.track.removeEventListener(
        'scroll',
        this.handleScroll
      );

      this.track.removeEventListener(
        'pointerdown',
        this.handlePointerDown
      );

      this.track.removeEventListener(
        'pointermove',
        this.handlePointerMove
      );

      this.track.removeEventListener(
        'click',
        this.handleTrackClick,
        true
      );

      if (this.previousButton) {
        this.previousButton.removeEventListener(
          'click',
          this.showPrevious
        );
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener(
          'click',
          this.showNext
        );
      }

      if (this.desktopBreakpoint) {
        if (this.desktopBreakpoint.removeEventListener) {
          this.desktopBreakpoint.removeEventListener(
            'change',
            this.handleBreakpointChange
          );
        } else {
          this.desktopBreakpoint.removeListener(
            this.handleBreakpointChange
          );
        }
      }

      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }

      this.dataset.initialized = 'false';
    }

    handlePointerDown(event) {
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.wasDragged = false;
    }

    handlePointerMove(event) {
      const distanceX = Math.abs(
        event.clientX - this.startX
      );

      const distanceY = Math.abs(
        event.clientY - this.startY
      );

      if (distanceX > 8 && distanceX > distanceY) {
        this.wasDragged = true;
      }
    }

    handleTrackClick(event) {
      if (!this.wasDragged) return;

      event.preventDefault();
      event.stopPropagation();
      this.wasDragged = false;
    }

    handleScroll() {
      if (
        this.desktopBreakpoint.matches ||
        this.rafId
      ) {
        return;
      }

      this.rafId = requestAnimationFrame(() => {
        const slideWidth = this.track.clientWidth;

        if (slideWidth > 0) {
          const index = Math.round(
            this.track.scrollLeft / slideWidth
          );

          this.currentIndex = Math.max(
            0,
            Math.min(index, this.slides.length - 1)
          );

          this.updateDots();
        }

        this.rafId = null;
      });
    }

    showPrevious(event) {
      event.preventDefault();
      event.stopPropagation();

      this.setDesktopSlide(
        this.currentIndex - 1
      );
    }

    showNext(event) {
      event.preventDefault();
      event.stopPropagation();

      this.setDesktopSlide(
        this.currentIndex + 1
      );
    }

    setDesktopSlide(index) {
      const total = this.slides.length;

      this.currentIndex =
        (index + total) % total;

      this.slides.forEach(
        (slide, slideIndex) => {
          const isActive =
            slideIndex === this.currentIndex;

          slide.classList.toggle(
            'is-active',
            isActive
          );

          slide.setAttribute(
            'aria-hidden',
            isActive ? 'false' : 'true'
          );

          slide.tabIndex = isActive ? 0 : -1;
        }
      );

      this.updateDots();
    }

    updateDots() {
      this.dots.forEach((dot, index) => {
        dot.classList.toggle(
          'is-active',
          index === this.currentIndex
        );
      });
    }

    handleBreakpointChange() {
      this.applyCurrentMode();
    }

    applyCurrentMode() {
      if (this.desktopBreakpoint.matches) {
        const slideWidth = this.track.clientWidth;

        if (slideWidth > 0) {
          const mobileIndex = Math.round(
            this.track.scrollLeft / slideWidth
          );

          this.currentIndex = Math.max(
            0,
            Math.min(
              mobileIndex,
              this.slides.length - 1
            )
          );
        }

        this.track.scrollLeft = 0;
        this.setDesktopSlide(this.currentIndex);
        return;
      }

      this.slides.forEach((slide) => {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('tabindex');
      });

      requestAnimationFrame(() => {
        this.track.scrollLeft =
          this.track.clientWidth *
          this.currentIndex;

        this.updateDots();
      });
    }
  }

  customElements.define(
    'product-card-carousel',
    ProductCardCarousel
  );
}